import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetTestKitById,
  SaveTestKit,
  UpdateTestKit,
  GetProducts,
} from "../../../services/InventoryService";
import CommonLoader from "../../common/CommonLoader";

const AddEditTestKit = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(idParam);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [kitName, setKitName] = useState("");
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts();
    if (isEdit) fetchTestKit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  const fetchProducts = async () => {
    try {
      const response = await GetProducts();
      setProducts(response?.results ?? []);
    } catch {
      NotificationManager.error("Error loading products");
    }
  };

  const fetchTestKit = async () => {
    setLoading(true);
    try {
      const response = await GetTestKitById(idParam);
      const k = response?.results ?? response;
      if (!k) return;
      setKitName(k.kitName || "");
      setSku(k.sku || "");
      setDescription(k.description || "");
      setNotes(k.notes || "");
      setIsActive(k.isActive === undefined ? true : k.isActive);
      setItems(k.items || []);
    } catch {
      NotificationManager.error("Error loading test kit");
    } finally {
      setLoading(false);
    }
  };

  const findProduct = (productId) =>
    products.find((p) => p.id === parseInt(productId, 10));

  // Variant cost wins, otherwise the product cost - same rule the API uses.
  const unitCost = (item) => {
    const product = findProduct(item.productId);
    const variant = (product?.variants || []).find(
      (v) => v.id === parseInt(item.productVariantId, 10)
    );
    return Number(variant?.cost ?? product?.cost ?? 0) || 0;
  };

  const lineCost = (item) => (Number(item.quantity) || 0) * unitCost(item);

  const totalCost = items.reduce((sum, item) => sum + lineCost(item), 0);

  const handleAddItem = () => {
    setItems([
      ...items,
      { id: 0, productId: "", productVariantId: null, quantity: 1, notes: "" },
    ]);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };

    if (field === "productId") {
      updated[index].productVariantId = null;
    }

    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const buildPayload = () => ({
    id: isEdit ? parseInt(idParam, 10) : 0,
    kitName: kitName.trim(),
    sku,
    description,
    notes,
    isActive,
    items: items
      .filter((item) => item.productId)
      .map((item) => ({
        id: item.id || 0,
        testKitId: isEdit ? parseInt(idParam, 10) : 0,
        productId: parseInt(item.productId, 10),
        productVariantId: item.productVariantId
          ? parseInt(item.productVariantId, 10)
          : null,
        quantity: Number(item.quantity) || 0,
        notes: item.notes,
      })),
  });

  const handleSubmit = async () => {
    if (!kitName.trim()) {
      NotificationManager.error("Kit name is required");
      return;
    }

    const lines = items.filter((item) => item.productId);
    if (!lines.length) {
      NotificationManager.error("Add at least one component to the kit");
      return;
    }

    if (lines.some((item) => !(Number(item.quantity) > 0))) {
      NotificationManager.error("Each component needs a quantity above 0");
      return;
    }

    // Components are tracked at variant level whenever the product has variants.
    const missingVariant = lines.find(
      (item) =>
        (findProduct(item.productId)?.variants || []).length > 0 &&
        !item.productVariantId
    );
    if (missingVariant) {
      NotificationManager.error(
        `Pick a variant for ${findProduct(missingVariant.productId)?.productName}`
      );
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await UpdateTestKit(buildPayload());
        NotificationManager.success("Test kit updated successfully");
      } else {
        await SaveTestKit(buildPayload());
        NotificationManager.success("Test kit created successfully");
      }
      navigate("/vlp/inventory-test-kits");
    } catch (error) {
      // A duplicate kit name comes back as a 409.
      if (error?.response?.status === 409) {
        NotificationManager.error("A test kit with this name already exists");
      } else {
        NotificationManager.error("Error saving test kit");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate("/vlp/inventory-test-kits");
  };

  const tf = (label, value, setter, extra = {}) => (
    <TextField
      size="small"
      label={label}
      value={value}
      onChange={(e) => setter(e.target.value)}
      fullWidth
      {...extra}
    />
  );

  return (
    <div className="vlp-component-container">
      <span className="title">{isEdit ? "Edit Test Kit" : "Add Test Kit"}</span>
      <hr />

      <Paper elevation={2} sx={{ p: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={3}>
            {tf("Kit Name *", kitName, setKitName, {
              inputProps: { maxLength: 200 },
            })}
          </Grid>
          <Grid item xs={2}>
            {tf("SKU", sku, setSku, { inputProps: { maxLength: 100 } })}
          </Grid>
          <Grid item xs={2}>
            <TextField
              size="small"
              label="Total Cost"
              fullWidth
              value={totalCost.toFixed(2)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(isActive)}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item xs={6}>
            {tf("Description", description, setDescription, {
              inputProps: { maxLength: 1000 },
            })}
          </Grid>
          <Grid item xs={6}>
            {tf("Notes", notes, setNotes, { inputProps: { maxLength: 2000 } })}
          </Grid>

          {/* Actions */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button variant="outlined" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Components */}
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Typography variant="subtitle2">Components</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Component
          </Button>
        </div>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Variant</TableCell>
              <TableCell>Qty per Kit</TableCell>
              <TableCell>Unit Cost</TableCell>
              <TableCell>Line Cost</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const product = findProduct(item.productId);
              const productVariants = product?.variants || [];

              return (
                <TableRow key={`item-${index}`}>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.productId || ""}
                      onChange={(e) =>
                        handleItemChange(index, "productId", e.target.value)
                      }
                      sx={{ minWidth: 180 }}
                    >
                      {products.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={item.productVariantId || ""}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "productVariantId",
                          e.target.value
                        )
                      }
                      disabled={!productVariants.length}
                      sx={{ minWidth: 130 }}
                    >
                      {productVariants.map((variant) => (
                        <MenuItem key={variant.id} value={variant.id}>
                          {variant.variantName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={item.quantity ?? 1}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      inputProps={{ min: 0 }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell>${unitCost(item).toFixed(2)}</TableCell>
                  <TableCell>${lineCost(item).toFixed(2)}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.notes || ""}
                      onChange={(e) =>
                        handleItemChange(index, "notes", e.target.value)
                      }
                      inputProps={{ maxLength: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      style={{ color: "red" }}
                      onClick={() => handleRemoveItem(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    No components yet. Add the products that go into one kit.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <CommonLoader loading={loading || saving} />
    </div>
  );
};

export default AddEditTestKit;
